
import React from 'react';
import { Button, ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface LuxuryButtonProps extends Omit<ButtonProps, 'variant'> {
  variant?: 'primary' | 'secondary' | 'glass' | 'ghost';
  glowEffect?: boolean;
  gradientPulse?: boolean;
  appleStyle?: boolean;
}

const LuxuryButton = ({ 
  className, 
  variant = 'primary',
  glowEffect = false,
  gradientPulse = false,
  appleStyle = true,
  children,
  asChild = false,
  ...props 
}: LuxuryButtonProps) => {
  const baseClasses = appleStyle 
    ? "transition-all duration-300 ease-out apple-hover-scale font-medium"
    : "relative overflow-hidden transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 font-medium";
  
  const variantClasses = {
    primary: appleStyle 
      ? "apple-button-primary"
      : cn(
          "bg-juv-gradient-primary text-white font-semibold",
          "shadow-apple-lg hover:shadow-apple-xl",
          "border border-juv-rose-clay/20"
        ),
    secondary: appleStyle
      ? "apple-button-secondary"
      : "bg-white/90 backdrop-blur-lg border border-juv-rose-clay/50 text-juv-terracotta hover:bg-juv-soft-blush/80 shadow-apple hover:shadow-apple-lg",
    glass: appleStyle
      ? "bg-white/30 backdrop-blur-xl border border-white/40 text-juv-deep-chestnut hover:bg-white/40 shadow-apple hover:shadow-apple-lg rounded-full px-8 py-4"
      : "bg-white/30 backdrop-blur-lg border border-white/40 text-juv-deep-chestnut hover:bg-white/40 shadow-apple hover:shadow-apple-lg",
    ghost: appleStyle
      ? "apple-button-ghost"
      : "bg-transparent text-juv-deep-chestnut hover:bg-juv-soft-blush/30 transition-all duration-300"
  };

  const glowClasses = glowEffect ? "hover:shadow-apple-xl" : "";
  const gradientPulseClasses = gradientPulse ? "animate-gradient-pulse" : "";

  // When asChild is true, we need to ensure only one child element
  if (asChild) {
    return (
      <Button 
        asChild
        variant="ghost"
        className={cn(
          baseClasses,
          variantClasses[variant],
          glowClasses,
          gradientPulseClasses,
          className
        )} 
        {...props}
      >
        <div className="relative">
          <span className="relative z-10">{children}</span>
          {!appleStyle && variant === 'primary' && (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700 ease-out"></div>
          )}
        </div>
      </Button>
    );
  }

  return (
    <Button 
      variant="ghost"
      className={cn(
        baseClasses,
        variantClasses[variant],
        glowClasses,
        gradientPulseClasses,
        className
      )} 
      {...props}
    >
      <span className="relative z-10">{children}</span>
      {!appleStyle && variant === 'primary' && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700 ease-out"></div>
      )}
    </Button>
  );
};

export default LuxuryButton;
